import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { Grid, Paper, ShipsIcon, Tabs, Tab, Slide, Stack } from '@esgian/esgianui';
import { TextWithTooltipIcon } from '@components';
import { YtdPortCallStatsTable } from '@components/Tables';

import { DATE_FORMAT, GEOGRAPHICAL_TYPES } from '@constants';
import PortWaitingTimeSection from '../PortProfileSection/SubSections/PortWaitingTimeSection';
import { useDispatch, useSelector } from 'react-redux';
import { getCommercialCategoryType, getOperatorType, setGeographicalType } from '@store/features';
import KpiBox from '@components/Display/KpiBox';
import { useCountryPortStats, useSegment } from '@hooks';
import { useQuery } from '@tanstack/react-query';
import {
  getActivePorts,
  getCountryTopPorts,
  getPortCallSummary,
  getPortCongestion,
  getPortsDetails
} from '@api';
import CountryOverviewSection from '@components/Sections/ShipAnalytics/CountryProfileSection/CountryOverviewSection';
import CountryStatisticsSection from '@components/Sections/ShipAnalytics/CountryProfileSection/CountryStatisticsSection';
import { toast } from 'react-toastify';

function CountryProfileSection({ countryName }) {
  const [section, setSection] = useState(1);
  const [init, setInit] = useState(false);
  const { segment, segmentMinDate } = useSegment();
  const operatorType = useSelector(getOperatorType);
  const params = useParams();
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const countryPortStatsQuery = useCountryPortStats(Number(params.countryId));
  const { ytdNumPortCalls, prevYearYtdNumPortCalls, prevYearNumPortCalls } =
    countryPortStatsQuery.data ?? {};

  const handleSectionChange = useCallback(
    (_, val) => {
      if (init) {
        searchParams.set('section', val);
        setSearchParams(searchParams);
        setSection(Number(val));
      }
    },
    [init]
  );

  useEffect(() => {
    dispatch(setGeographicalType(GEOGRAPHICAL_TYPES.COUNTRY));
    if (searchParams.has('section')) {
      setSection(Number(searchParams.get('section')));
    }
    setInit(true);
    return () => {
      searchParams.delete('section');
    };
  }, []);

  useEffect(() => {
    if (!countryPortStatsQuery.data?.polyfit) {
      handleSectionChange(null, 1);
    }
  }, [countryPortStatsQuery]);

  const countryTopPortsQuery = useQuery({
    enabled: section === 3,
    placeholderData: null,
    queryKey: [
      'countryTopPorts',
      {
        CountryId: Number(params.countryId),
        NumberOfPorts: 20,
        NumberOfOperators: 20,
        Year: selectedYear,
        OperatorTypeId: operatorType,
        SegmentTypeId: segment.id,
        commercialCategoryId: commercialCategoryType
      }
    ],
    queryFn: ({ signal, queryKey }) =>
      getCountryTopPorts(queryKey[1], signal).then((res) => res.topPortsYTD)
  });

  const countryPortDetailsQuery = useQuery({
    queryKey: [
      'activeCountryPorts',
      {
        geoType: GEOGRAPHICAL_TYPES.PORT,
        segmentTypeId: segment.id,
        operatorTypeId: operatorType,
        countryId: params.countryId,
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!segment.id,
    placeholderData: [],
    queryFn: async ({ queryKey, signal }) => {
      const queryParams = queryKey[1];

      try {
        const ports = await getActivePorts(queryParams, signal);

        let portIds = ports.map(({ geoId }) => geoId);

        let [details, portCall] = await Promise.all([
          getPortsDetails(signal, {
            region: false,
            terminals: false,
            ids: portIds
          }).then((resp) => {
            let res = {};
            resp.forEach((item) => {
              res[item.id] = item;
            });
            return res;
          }),
          getPortCallSummary({ ...queryParams, geoIds: portIds }, signal)
        ]);
        let result = [];
        Object.values(details).forEach((port) => {
          if (port.country.id !== Number(params.countryId)) return;
          let portCallData = portCall[port.id] ?? {};

          result.push({ ...port, ...portCallData });
        });
        return result;
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error(`Unable to fetch active ports`, {
            position: 'top-right'
          });
        }
        return [];
      }
    }
  });

  const portCongestionQuery = useQuery({
    enabled: section === 4,
    placeholderData: null,
    queryKey: [
      'countryCongestion',
      {
        start: moment(segmentMinDate).format(DATE_FORMAT),
        end: moment().format(DATE_FORMAT),
        GeoType: GEOGRAPHICAL_TYPES.COUNTRY,
        OperatorTypeId: operatorType,
        SegmentTypeId: segment.id,
        commercialCategoryId: commercialCategoryType,
        GeoId: Number(params.countryId)
      }
    ],
    queryFn: ({ signal, queryKey }) => getPortCongestion(queryKey[1], signal)
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          spacing={1} // Applies spacing between items on the same row
          useFlexGap
          sx={{ flexWrap: 'wrap' }}>
          <KpiBox
            loading={countryPortStatsQuery.isFetching}
            icon={<ShipsIcon />}
            value={ytdNumPortCalls ?? ''}
            title={'Port Calls (YTD)'}
          />
          <KpiBox
            loading={countryPortStatsQuery.isFetching}
            icon={<ShipsIcon />}
            value={prevYearYtdNumPortCalls ?? ''}
            title={`${moment().subtract(12, 'months').format('yyyy')} Port Calls YTD`}
          />
          <KpiBox
            loading={countryPortStatsQuery.isFetching}
            icon={<ShipsIcon />}
            value={prevYearNumPortCalls ?? ''}
            title={`${moment().subtract(12, 'months').format('yyyy')} # Port Calls`}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} id={'country-profile-overview'}>
        <Paper>
          <Stack direction={'row'} spacing={2} alignItems={'center'}>
            <Tabs value={section} onChange={handleSectionChange} variant="scrollable">
              <Tab value={1} label="Overview" />
              {countryPortStatsQuery.data?.polyfit && <Tab value={2} label={'port statistics'} />}
              <Tab value={3} label="Top Ports in Country" />
              <Tab value={4} label="Port Waiting Time" />
            </Tabs>
            {!countryPortStatsQuery.data?.polyfit && (
              <TextWithTooltipIcon
                color={'text.secondary'}
                label="PORT STATISTICS"
                tooltipText="Insufficient data to compute the statistics"
              />
            )}
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        {section === 1 && (
          <CountryOverviewSection countryPortDetailsQuery={countryPortDetailsQuery} />
        )}
        {section === 2 && (
          <CountryStatisticsSection
            countryPortDetailsQuery={countryPortDetailsQuery}
            countryName={countryName}
          />
        )}
        {section === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <YtdPortCallStatsTable
                loading={countryTopPortsQuery.isFetching}
                topPortsData={countryTopPortsQuery.data}
                handleSelectYear={({ target }) => setSelectedYear(Number(target.value))}
                selectedYear={selectedYear}
                subTableHeader={`Operators YTD ${selectedYear}`}
              />
            </Grid>
          </Grid>
        )}
        {section === 4 && (
          <PortWaitingTimeSection
            assetLevel={'port'}
            portCongestionData={portCongestionQuery.data}
            loading={portCongestionQuery.isFetching}
          />
        )}
      </Grid>
    </Grid>
  );
}

CountryProfileSection.propTypes = {
  countryName: PropTypes.string
};

CountryProfileSection.defaultProps = {
  countryName: ''
};

export default CountryProfileSection;
